import React, { useState, useEffect } from "react";
import api from "../config/api";
import "./AddEnterpriseModal.css";
import { toast } from "react-toastify";
import Select from "react-select";
import { formSelectStyles } from "../utils/selectStyles";
import { LANGUAGE_OPTIONS } from "../utils/language";

interface EditEnterpriseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (enterprise: any) => void;
  enterprise: any;
}

const EditEnterpriseModal: React.FC<EditEnterpriseModalProps> = ({
  isOpen,
  onClose,
  onSave,
  enterprise,
}) => {
  const [formData, setFormData] = useState({
    nom: "",
    sirene: "",
    adresse: "",
    mail: "",
    country: "",
    language: "en",
    sendContracts: true,
  });
  const [loading, setLoading] = useState(false);
  const [coders, setCoders] = useState<any[]>([]);
  const [loadingCoders, setLoadingCoders] = useState(false);
  const [currentCoders, setCurrentCoders] = useState<any[]>([]);
  const [selectedCoderId, setSelectedCoderId] = useState<number | null>(null);

  useEffect(() => {
    if (enterprise) {
      // The enterprises endpoint already joins `coders`, so the tags can be shown
      // on the first paint. Waiting for fetchAllCoders() meant the section grew
      // from one line of text to a row of tags, resizing the whole dialog about
      // half a second after it opened.
      setCurrentCoders(enterprise.coders ?? []);
      setFormData({
        nom: enterprise.nom,
        sirene: enterprise.sirene || "",
        adresse: enterprise.adresse || "",
        mail: enterprise.mail || "",
        country: enterprise.country,
        language: enterprise.language,
        sendContracts:
          enterprise.sendContracts !== undefined
            ? enterprise.sendContracts
            : true,
      });
    }
  }, [enterprise]);

  useEffect(() => {
    if (isOpen && enterprise) {
      fetchAllCoders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, enterprise?.id]);

  const fetchAllCoders = async () => {
    try {
      setLoadingCoders(true);
      // UN SEUL appel API pour récupérer tous les codeurs
      const response = await api.get("/api/coders?limit=10000");
      const allCoders = response.data.data || [];

      // Séparer les codeurs de cette entreprise des autres
      const enterpriseCoders = allCoders.filter(
        (coder: any) => coder.enterprise?.id === enterprise?.id,
      );

      setCoders(allCoders);
      setCurrentCoders(enterpriseCoders);
    } catch (error) {
      console.error("Error loading coders:", error);
      toast.error("Erreur lors du chargement des codeurs");
    } finally {
      setLoadingCoders(false);
    }
  };

  const handleAddCoder = async () => {
    if (!selectedCoderId) return;

    try {
      await api.put(`/api/coders/${selectedCoderId}`, {
        enterpriseId: enterprise.id,
      });
      toast.success("Codeur ajouté à l'entreprise avec succès !");
      setSelectedCoderId(null);
      await fetchAllCoders();
    } catch (error: any) {
      console.error("Error linking coder to enterprise:", error);
      toast.error(
        "Erreur lors de la liaison du codeur : " +
          (error.response?.data?.error || "Erreur inconnue"),
      );
    }
  };

  const handleRemoveCoder = async (coderId: number) => {
    if (
      !window.confirm(
        "Voulez-vous vraiment retirer ce codeur de l'entreprise ?",
      )
    ) {
      return;
    }

    try {
      await api.put(`/api/coders/${coderId}`, {
        enterpriseId: null,
      });
      toast.success("Codeur retiré de l'entreprise avec succès !");
      await fetchAllCoders();
    } catch (error: any) {
      console.error("Error removing coder from enterprise:", error);
      toast.error(
        "Erreur lors du retrait du codeur : " +
          (error.response?.data?.error || "Erreur inconnue"),
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Update enterprise
      const response = await api.put(`/api/enterprises/${enterprise.id}`, {
        nom: formData.nom,
        sirene: formData.sirene || null,
        adresse: formData.adresse || null,
        mail: formData.mail || null,
        country: formData.country,
        language: formData.language,
        sendContracts: formData.sendContracts,
      });

      onSave(response.data);
      onClose();
      toast.success("Entreprise mise à jour avec succès!");
    } catch (error: any) {
      console.error("Error updating enterprise:", error);

      let errorMessage = "Erreur lors de la mise à jour de l'entreprise.";

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 500) {
        errorMessage = "Erreur serveur. Veuillez vérifier vos données.";
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Modifier l'Entreprise</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="nom">Nom de l'Entreprise</label>
            <input
              type="text"
              id="nom"
              name="nom"
              value={formData.nom}
              onChange={handleChange}
              required
              placeholder="Nom de l'entreprise"
            />
          </div>

          <div className="form-group">
            <label htmlFor="sirene">SIREN</label>
            <input
              type="text"
              id="sirene"
              name="sirene"
              value={formData.sirene}
              onChange={handleChange}
              placeholder="Numéro SIREN"
            />
          </div>

          <div className="form-group">
            <label htmlFor="adresse">Adresse</label>
            <textarea
              id="adresse"
              name="adresse"
              value={formData.adresse}
              onChange={handleChange}
              placeholder="Adresse de l'entreprise"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="mail">Email</label>
            <input
              type="email"
              id="mail"
              name="mail"
              value={formData.mail}
              onChange={handleChange}
              placeholder="Email de l'entreprise"
            />
          </div>

          <div className="form-group">
            <label htmlFor="country">Pays</label>
            <input
              type="text"
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
              placeholder="Pays"
            />
          </div>

          <div className="form-group">
            <label htmlFor="language">Langue</label>
            <select
              id="language"
              name="language"
              value={formData.language}
              onChange={handleChange}
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Section Codeurs liés */}
          <div className="form-group">
            <label>Codeurs de l'entreprise</label>
            {/* One scrolling row of fixed height: the dialog must not grow as coders
                are added or removed. The empty state sits in the same row, so the
                height is identical either way.
                Note this is NOT `.coders-list` — that is the table cell's variant,
                capped at 300px and wrapping, which stacked the tags one per line. */}
            <div className="coders-row">
              {currentCoders.length > 0 ? (
                currentCoders.map((coder) => (
                  <div key={coder.id} className="coder-tag-with-remove">
                    <span>
                      {coder.first_name} {coder.last_name}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveCoder(coder.id)}
                      title="Retirer ce codeur"
                    >
                      ×
                    </button>
                  </div>
                ))
              ) : (
                <p className="coders-row-empty">
                  {loadingCoders
                    ? "Chargement des codeurs..."
                    : "Aucun codeur assigné à cette entreprise"}
                </p>
              )}
            </div>
          </div>

          {/* Sélection d'un nouveau codeur */}
          <div className="form-group">
            <label htmlFor="coderId">Ajouter un Codeur</label>
            {/* `stretch` rather than a second hardcoded height: the button matches
                whatever the select measures, so the two stay level if the control
                ever grows. */}
            <div style={{ display: "flex", gap: "10px", alignItems: "stretch" }}>
              <div style={{ flex: 1 }}>
                <Select
                  id="coderId"
                  name="coderId"
                  value={
                    selectedCoderId
                      ? {
                          value: selectedCoderId,
                          label: coders.find((c) => c.id === selectedCoderId)
                            ? `${
                                coders.find((c) => c.id === selectedCoderId)!
                                  .first_name
                              } ${
                                coders.find((c) => c.id === selectedCoderId)!
                                  .last_name
                              }`
                            : "",
                        }
                      : null
                  }
                  onChange={(option) =>
                    setSelectedCoderId(option?.value || null)
                  }
                  options={coders
                    .filter((coder) => {
                      // Exclure les codeurs déjà assignés à cette entreprise
                      const isAlreadyAssigned = currentCoders.some(
                        (c) => c.id === coder.id,
                      );
                      // Inclure uniquement les codeurs sans entreprise (disponibles)
                      return !isAlreadyAssigned && !coder.enterprise;
                    })
                    .map((coder) => ({
                      value: coder.id,
                      label: `${coder.first_name} ${coder.last_name}`,
                    }))}
                  isLoading={loadingCoders}
                  isClearable
                  placeholder="Sélectionner un codeur..."
                  noOptionsMessage={() => "Aucun codeur disponible"}
                  styles={formSelectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <button
                type="button"
                className="btn-primary"
                onClick={handleAddCoder}
                disabled={!selectedCoderId}
                style={{ minWidth: "100px" }}
              >
                Ajouter
              </button>
            </div>
            <p
              style={{
                color: "rgba(255, 255, 255, 0.6)",
                fontSize: "0.85rem",
                marginTop: "8px",
              }}
            >
              Les codeurs assignés recevront leurs contrats à l'adresse email de
              l'entreprise
            </p>
          </div>

          <div className="form-group checkbox-group">
            <label
              htmlFor="sendContracts"
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                id="sendContracts"
                name="sendContracts"
                checked={!formData.sendContracts}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    sendContracts: !e.target.checked,
                  }))
                }
                style={{ marginRight: "8px" }}
              />
              Ne pas envoyer de contrats
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? "Mise à jour..." : "Mettre à jour l'Entreprise"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEnterpriseModal;
